import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Select,
  Group,
  Stack,
  Badge,
  Alert,
  Button,
  Text,
  Code,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle, IconRefresh } from "@tabler/icons-react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable, type Column } from "../../components/common/DataTable";
import {
  DetailDrawer,
  DetailSection,
  DetailField,
} from "../../components/common/DetailDrawer";
import { StatCard } from "../../components/common/StatCard";
import { payoutsApi } from "../../api/settlements";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { errorMessage } from "../../lib/axios";
import { formatMoney, formatDateTime } from "../../lib/format";
import { humanize } from "../../lib/labels";
import type { DriverPayout } from "../../types/domain";

const STATUS_COLOR: Record<string, string> = {
  COMPLETED: "green",
  PROCESSING: "blue",
  PENDING: "yellow",
  FAILED: "red",
};

/** Payload carries the real lifecycle; the transaction status is coarser. */
function payoutStatus(p: DriverPayout): string {
  return p.payload?.payoutStatus ?? "PENDING";
}

function driverName(p: DriverPayout): string {
  if (!p.driverID || typeof p.driverID === "string") return "—";
  return p.driverID.personalDetails?.name ?? "—";
}

export function PayoutsPage() {
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<DriverPayout | null>(null);
  const queryClient = useQueryClient();

  const { page, setPage, result, isLoading, error } =
    usePaginatedList<DriverPayout>({
      queryKey: ["payouts"],
      fetcher: payoutsApi.list,
      filters: { status },
    });

  const statusQuery = useQuery({
    queryKey: ["payouts-status"],
    queryFn: payoutsApi.status,
  });

  const reconcileMutation = useMutation({
    mutationFn: (id: string) => payoutsApi.reconcile(id),
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: "Reconciliation requested. Refresh in a moment.",
      });
      queryClient.invalidateQueries({ queryKey: ["payouts"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const columns: Column<DriverPayout>[] = [
    {
      key: "driverID",
      header: "Driver",
      render: (p) => driverName(p),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (p) => formatMoney(p.amount),
    },
    {
      key: "destination",
      header: "Destination",
      render: (p) => (
        <Code>{p.payload?.destination?.label ?? "—"}</Code>
      ),
    },
    {
      key: "payoutStatus",
      header: "Status",
      render: (p) => (
        <Badge color={STATUS_COLOR[payoutStatus(p)] ?? "gray"} variant="light">
          {humanize(payoutStatus(p))}
        </Badge>
      ),
    },
    {
      key: "receipt",
      header: "Gateway receipt",
      render: (p) => p.payload?.selcom?.receipt ?? <Text c="dimmed">—</Text>,
    },
    {
      key: "createdAt",
      header: "Requested",
      render: (p) => formatDateTime(p.createdAt),
    },
  ];

  const counts = statusQuery.data?.payouts ?? {};

  return (
    <Stack>
      <PageHeader
        title="Driver Cash-outs"
        subtitle="Money Smart Safiri has paid out to drivers through the gateway"
        actions={
          <Select
            placeholder="All statuses"
            data={["PENDING", "PROCESSING", "COMPLETED", "FAILED"]}
            value={status}
            onChange={(val) => {
              setStatus(val ?? "");
              setPage(1);
            }}
            clearable
            w={170}
          />
        }
      />

      <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
        A cash-out always pays a driver's <strong>entire</strong> credit balance
        — there are no partial withdrawals — and <strong>Smart Safiri absorbs</strong>{" "}
        the disbursement fee, so the driver receives the full amount. This list
        covers gateway-executed payouts only; money handed over outside the
        system is a manual settlement and appears under Transactions.
      </Alert>

      {statusQuery.data && !statusQuery.data.enabled && (
        <Alert variant="light" color="yellow">
          Cash-outs are <strong>not available</strong> — drivers cannot withdraw
          right now.
          {statusQuery.data.configurationError
            ? ` Reason: ${statusQuery.data.configurationError}`
            : ""}
        </Alert>
      )}

      <Group grow align="stretch">
        <StatCard label="Processing" value={String(counts.processing ?? counts.PROCESSING ?? 0)} sub="In flight" />
        <StatCard label="Completed" value={String(counts.completed ?? counts.COMPLETED ?? 0)} sub="Delivered" />
        <StatCard label="Failed" value={String(counts.failed ?? counts.FAILED ?? 0)} sub="Balance restored" />
        <StatCard label="Pending" value={String(counts.pending ?? counts.PENDING ?? 0)} sub="Awaiting the gateway" />
      </Group>

      <DataTable<DriverPayout>
        columns={columns}
        rows={result?.items ?? []}
        rowKey={(p) => p._id}
        loading={isLoading}
        error={error ? errorMessage(error) : null}
        page={page}
        totalPages={result?.totalPages}
        onPageChange={setPage}
        total={result?.total}
        onRowClick={setSelected}
        emptyLabel="No cash-outs yet"
      />

      <DetailDrawer
        opened={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="Cash-out"
      >
        {selected && (
          <Stack>
            <DetailSection title="Payout">
              <DetailField label="Driver" value={driverName(selected)} />
              <DetailField
                label="Amount"
                value={formatMoney(selected.amount)}
              />
              <DetailField
                label="Status"
                value={humanize(payoutStatus(selected))}
              />
              <DetailField
                label="Gateway receipt"
                value={selected.payload?.selcom?.receipt ?? "—"}
              />
              <DetailField
                label="Requested"
                value={formatDateTime(selected.createdAt)}
              />
              {selected.payload?.settledAt && (
                <DetailField
                  label="Settled"
                  value={formatDateTime(selected.payload.settledAt)}
                />
              )}
              {selected.payload?.lastError && (
                <DetailField
                  label="Last error"
                  value={selected.payload.lastError}
                />
              )}
            </DetailSection>

            <DetailSection title="Destination">
              <DetailField
                label="Type"
                value={humanize(selected.payload?.destination?.type ?? "")}
              />
              <DetailField
                label="Institution"
                value={selected.payload?.destination?.fiCode ?? "—"}
              />
              <DetailField
                label="Account"
                value={selected.payload?.destination?.accountNumber ?? "—"}
              />
              <DetailField
                label="Account name"
                value={selected.payload?.destination?.accountName ?? "—"}
              />
            </DetailSection>

            <DetailSection title="Ledger effect">
              <DetailField
                label="Posted to balance"
                value={
                  selected.payload?.debtDeltaApplied
                    ? formatMoney(selected.payload.debtDeltaApplied)
                    : "Not posted"
                }
              />
              {Boolean(selected.payload?.debtDeltaReversed) && (
                <DetailField
                  label="Reversed"
                  value={formatMoney(selected.payload!.debtDeltaReversed!)}
                />
              )}
              <Text size="xs" c="dimmed">
                The balance is moved when the gateway <em>accepts</em> the
                transfer, not when it settles — at that point the money is
                already committed. A later failure posts a compensating
                reversal, which is what the second row shows.
              </Text>
            </DetailSection>

            {selected.payload?.needsReconciliation && (
              <Alert variant="light" color="orange">
                The gateway never confirmed whether it accepted this transfer.
                It has been left pending on purpose — re-sending could pay the
                driver twice. Reconciling asks the gateway what happened.
              </Alert>
            )}

            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              loading={reconcileMutation.isPending}
              onClick={() => reconcileMutation.mutate(selected._id)}
            >
              Reconcile with gateway
            </Button>
          </Stack>
        )}
      </DetailDrawer>
    </Stack>
  );
}
